// src/pages/LeadGeneratorDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReplyIcon from '@mui/icons-material/Reply';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ErrorIcon from '@mui/icons-material/Error';
import ForumIcon from '@mui/icons-material/Forum';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { getUserFriendlyError } from '../utils/errorHandler';
import AutorenewIcon from "@mui/icons-material/Autorenew";

interface Action {
  action_id: string;
  action_type: string;
  chat_name?: string;
  user_id?: number;
  trigger_keywords?: string[];
  original_message?: string;
  response_text?: string;
  created_at: string;
}

interface ChatStat {
  triggers_fired: number;
  messages_sent: number;
  replies_received: number;
  errors: number;
}

interface Detail {
  campaign_id: string;
  account_name: string;
  status: string;
  monitoring_chats_count: number;
  total_triggers_fired: number;
  total_messages_sent: number;
  total_replies_received: number;
  total_errors: number;
  chat_stats: Record<string, ChatStat>;
  template_stats: Record<string, { used: number; replies: number }>;
  last_trigger?: any;
  last_message_sent?: any;
  last_error?: any;
  started_at?: string;
  last_activity_at?: string;
  uptime_seconds?: number;
  total_leads_duplicated: number;
  auto_responder_conversations: number;
  actions: Action[];
}

const LeadGeneratorDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const actionTypeLabels: Record<string, string> = {
  sent: 'Сообщение отправлено',
  triggered: 'Сработал триггер',
  error: 'Ошибка',
    auto_responded: 'Автоответчик',
    duplicated: 'Дублировано в чат'
};
  useEffect(() => {
    if (!jobId) {
      navigate('/lead_generator');
    }
  }, [jobId, navigate]);

  const fetchData = async () => {
    if (!jobId) return;

    try {
      const [statsRes, actionsRes] = await Promise.all([
        api.get(`/lead_generator/status/${jobId}`),
        api.get(`/lead_generator/actions/${jobId}`)
      ]);

      setData({
        ...statsRes.data,
        actions: actionsRes.data || []
      });
      setError(null);
    } catch (err: any) {
      setError(getUserFriendlyError(err, [], 'детали кампании'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [jobId]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Кампания не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lead_generator')}>
          Назад к лидогенератору
        </Button>
      </Container>
    );
  }

  const uptime = Math.floor(data.uptime_seconds || 0);
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lead_generator')} sx={{ mb: 4 }}>
        Назад к лидогенератору
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Лидогенератор #{jobId.slice(0, 8)}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунт: <strong>{data.account_name}</strong> · Чатов: <strong>{data.monitoring_chats_count}</strong> ·
          <AccessTimeIcon sx={{ verticalAlign: 'middle', mx: 1 }} />
          Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
        </Typography>

        {/* Основные счётчики */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {data.total_triggers_fired}
                </Typography>
                <Typography color="text.secondary">Триггеров</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#3B82F6">
                  {data.total_messages_sent}
                </Typography>
                <Typography color="text.secondary">Отправлено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {data.total_replies_received}
                </Typography>
                <Typography color="text.secondary">Ответов</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {data.total_errors}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Доп. блоки */}
        <Grid container spacing={3} mb={6}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} mb={2}>
                <ForumIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Автоответчик в ЛС
              </Typography>
              <Typography variant="h4" color="#1E40AF">{data.auto_responder_conversations}</Typography>
              <Typography color="text.secondary">активных диалогов</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} mb={2}>
                <ContentCopyIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Дублированные лиды
              </Typography>
              <Typography variant="h4" color="#1E40AF">{data.total_leads_duplicated}</Typography>
              <Typography color="text.secondary">переслано в ваш чат</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* По чатам */}
        {Object.keys(data.chat_stats).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              По чатам
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Чат</strong></TableCell>
                    <TableCell align="right"><strong>Триггеры</strong></TableCell>
                    <TableCell align="right"><strong>Отправлено</strong></TableCell>
                    <TableCell align="right"><strong>Ответы</strong></TableCell>
                    <TableCell align="right"><strong>Ошибки</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(data.chat_stats).map(([chat, s]) => (
                    <TableRow key={chat}>
                      <TableCell>{chat}</TableCell>
                      <TableCell align="right">{s.triggers_fired}</TableCell>
                      <TableCell align="right" sx={{ color: '#3B82F6', fontWeight: 600 }}>{s.messages_sent}</TableCell>
                      <TableCell align="right">{s.replies_received}</TableCell>
                      <TableCell align="right" sx={{ color: s.errors > 0 ? '#DC2626' : 'inherit' }}>
                        {s.errors}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}



        {/* Ошибка */}
        {data.last_error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <strong>Последняя ошибка:</strong> {typeof data.last_error === 'string' ? data.last_error : data.last_error.message || 'Неизвестная ошибка'}
            <br />
            <Typography component="span" fontSize="0.85rem">
              {data.last_error.timestamp ? new Date(data.last_error.timestamp).toLocaleString('ru-RU') : '—'}
            </Typography>
          </Alert>
        )}

        {/* ТАБЛИЦА ДЕЙСТВИЙ — НА МЕСТЕ, КАК БЫЛА */}
        <Typography variant="h6" gutterBottom color="#1E40AF" fontWeight={600}>
          Последние действия
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 6 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Время</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Чат</TableCell>
                <TableCell>Триггер</TableCell>
                <TableCell>Сообщение</TableCell>
                <TableCell>Ответ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.actions.slice(0, 20).map(a => (
                <TableRow key={a.action_id}>
                  <TableCell>{new Date(a.created_at).toLocaleTimeString()}</TableCell>
                  <TableCell>
                    {a.action_type === 'sent' && (
                        <ReplyIcon fontSize="small" color="primary"/>
                    )}
                    {a.action_type === 'duplicated' && (
                        <AutorenewIcon fontSize="small" color="primary"/>
                    )}
                    {a.action_type === 'auto_responded' && (
                        <ReplyIcon fontSize="small" color="success"/>
                    )}
                    {a.action_type === 'triggered' && (
                        <SmartToyIcon fontSize="small" color="success"/>
                    )}

                    {a.action_type === 'error' && (
                        <ErrorIcon fontSize="small" color="error"/>
                    )}

                    {' '}
                    {actionTypeLabels[a.action_type] || a.action_type}
                  </TableCell>
                  <TableCell>{a.chat_name || 'ЛС'}</TableCell>
                  <TableCell>{a.trigger_keywords?.join(', ') || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                    {a.original_message || '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
                    {a.response_text || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Инфо */}
        <Grid container spacing={3} sx={{ mt: 2, color: '#4B5563' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <strong>Запущен:</strong> {data.started_at ? new Date(data.started_at).toLocaleString('ru-RU') : '—'}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <strong>Последняя активность:</strong>{' '}
            {data.last_activity_at ? new Date(data.last_activity_at).toLocaleString('ru-RU') : '—'}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default LeadGeneratorDetail;